import React, { useEffect, useRef, useState } from 'react';
import MaterialTable, { Icons } from 'material-table';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { ArrowDropDown, ArrowDropUp } from '@material-ui/icons';
import { useHistory } from 'react-router-dom';
import NavRoutes from '../../constants/NavRoutes';
import { BASE_ROUTE } from '../../constants/BaseRoute';
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText
} from '@material-ui/core';
import SearchField from '../../components/search/SearchField';
import DateTimeUtils from '../../../../shared/src/utils/DateTimeUtils';
import {
  getAllSurgeonForms,
  getSurgeonFormById,
  downloadSurgeonFormById,
  uploadSurgeonForm
} from '../../../../shared/src/services/SurgeonFormsService';
import { getAllSurgeons } from '../../../../shared/src/services/SurgeonService';
import '../surgeon/SurgeonStyle.scss';

const useStyles = makeStyles(() => ({
  root: {
    '& .MuiToolbar-regular': {
      minHeight: 0
    }
  },
  addButton: {
    background: '#F7931E',
    borderRadius: 8,
    width: '6.1vw',
    minWidth: 95,
    height: '2.5vw',
    minHeight: 36,
    textTransform: 'none',
    color: '#ffffff',
    '&:hover': {
      background: '#ef8712'
    }
  },
  dialog: {
    '& .MuiDialog-paperScrollPaper': {
      borderRadius: 8,
      boxShadow: '0 10px 30px 0 rgba(0, 0, 0, 0.05)'
    }
  },
  formField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 4,
      minHeight: 52,
      fontFamily: 'hindGunturMedium',
      fontSize: '1vw'
    },
    '& .MuiInputLabel-outlined': {
      fontFamily: 'hindGunturMedium',
      fontSize: '0.95vw'
    }
  },
  selectMenuItem: {
    fontFamily: 'hindGunturMedium',
    fontSize: '0.95vw'
  },
  actionButton: {
    borderRadius: 8,
    width: 95,
    height: 45,
    textTransform: 'none'
  },
  saveButton: {
    background: '#F7931E',
    color: '#ffffff',
    '&:hover': {
      background: '#ef8712'
    }
  },
  fileInputLabel: {
    fontFamily: 'hindGunturMedium',
    fontSize: '0.95vw',
    color: '#777B7E'
  },
  fileInput: {
    marginTop: 8
  },
  selectedFileName: {
    marginTop: 6,
    fontFamily: 'hindGunturMedium',
    fontSize: '0.9vw',
    color: '#161719'
  },
  actionLink: {
    color: '#0057B8',
    cursor: 'pointer',
    fontSize: '0.85vw',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline'
    }
  }
}));

interface SurgeonFormRow {
  id: number;
  form_name: string;
  created_on: string;
  assigned_surgeons_count: number;
}

interface SurgeonOption {
  id: number;
  name: string;
  surgeon_id?: string | number;
}

const SurgeonForms: React.FC = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const [forms, setForms] = useState<SurgeonFormRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [formName, setFormName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [surgeonOptions, setSurgeonOptions] = useState<SurgeonOption[]>([]);
  const [selectedSurgeonIds, setSelectedSurgeonIds] = useState<number[]>([]);
  const [searchText, setSearchText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editFormId, setEditFormId] = useState<number | null>(null);
  const [editFileId, setEditFileId] = useState<number | null>(null);
  const [editFileName, setEditFileName] = useState('');
  const searchTimeout = useRef<number | undefined>(undefined);
  const stylesObject = {
    width: '100%',
    fontFamily: 'hindGunturMedium',
    fontSize: '0.9vw'
  };
  const optionsObject = {
    search: false,
    paging: false,
    header: true,
    showTitle: false,
    sorting: true,
    headerStyle: {
      color: '#777B7E',
      fontFamily: 'workSansSemiBold',
      height: '3vw',
      fontSize: '0.75vw',
      textTransform: 'uppercase' as const
    },
    rowStyle: {
      cursor: 'pointer'
    }
  };
  const tableIcons: Partial<Icons> = {
    SortArrow: React.forwardRef<SVGSVGElement, { className?: string }>((props, ref) =>
      props.className?.includes('MuiTableSortLabel-active') ? (
        props.className?.includes('MuiTableSortLabel-iconDirectionDesc') ? (
          <ArrowDropDown
            {...props}
            ref={ref}
            style={{ color: '#000000', fontSize: '1.5rem' }}
          />
        ) : (
          <ArrowDropUp {...props} ref={ref} style={{ color: '#000000', fontSize: '1.5rem' }} />
        )
      ) : (
        <ArrowDropUp
          {...props}
          ref={ref}
          style={{ color: '#777B7E', fontSize: '1.5rem', opacity: 0.5 }}
        />
      )
    )
  };

  const fetchForms = async (searchValue = '') => {
    try {
      setLoading(true);
      const response = await getAllSurgeonForms(searchValue);
      const rows = response?.data?.forms || [];
      setForms(rows);
    } catch (e) {
      setForms([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSurgeonOptions = async () => {
    try {
      const allRows: SurgeonOption[] = [];
      let offset = 0;
      let totalCount = 0;
      do {
        const response = await getAllSurgeons(offset);
        const rows = response?.data?.rows || [];
        totalCount = response?.data?.count || 0;
        allRows.push(
          ...rows.map((r: any) => ({
            id: Number(r.id),
            name: r.name,
            surgeon_id: r.surgeon_id
          }))
        );
        offset += rows.length;
        if (rows.length === 0) {
          break;
        }
      } while (allRows.length < totalCount);
      setSurgeonOptions(allRows);
    } catch (e) {
      setSurgeonOptions([]);
    }
  };

  useEffect(() => {
    fetchForms();
    fetchSurgeonOptions();
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  const resetDialogState = () => {
    setEditFormId(null);
    setEditFileId(null);
    setEditFileName('');
    setFormName('');
    setFile(null);
    setSelectedSurgeonIds([]);
  };

  const openAddDialog = () => {
    resetDialogState();
    setIsAddOpen(true);
  };

  const handleEdit = async (row: SurgeonFormRow) => {
    const response = await getSurgeonFormById(row.id);
    const form = response?.data?.form;
    if (!form) {
      return;
    }
    setEditFormId(form.id);
    setEditFileId(form.file_id);
    setEditFileName(form.file_name || '');
    setFormName(form.form_name || '');
    const assignedIds =
      form.surgeon_mappings?.map((mapping: any) => Number(mapping.surgeon_id)).filter(Boolean) || [];
    setSelectedSurgeonIds(assignedIds);
    setFile(null);
    setIsAddOpen(true);
  };

  const handleDownload = async (row: SurgeonFormRow) => {
    try {
      const response = await downloadSurgeonFormById(row.id);
      const payload = response?.data || {};
      const fileName =
        payload?.meta_data?.file_name ||
        payload?.file_name ||
        `${row.form_name || 'surgeon-form'}-${row.id}.pdf`;
      const mimeType = payload?.mime_type || 'application/pdf';

      const raw = payload?.data ?? payload?.file_content;
      const bytes =
        raw && raw.type === 'Buffer' && Array.isArray(raw.data)
          ? new Uint8Array(raw.data)
          : raw instanceof ArrayBuffer
            ? new Uint8Array(raw)
            : Array.isArray(raw)
              ? new Uint8Array(raw)
              : null;

      if (!bytes || bytes.byteLength === 0) {
        return;
      }

      const blob = new Blob([bytes], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // silent failure for now
    }
  };

  const handleView = (row: SurgeonFormRow) => {
    history.push(
      `/${BASE_ROUTE}/${NavRoutes.PRIVATE.HOME}/${NavRoutes.PRIVATE.SURGEON_FORMS}/${row.id}/view`
    );
  };

  const handleSubmitNewForm = async () => {
    if (!formName.trim() || (!file && !editFileId)) {
      return;
    }
    try {
      setSubmitting(true);
      await uploadSurgeonForm({
        form_id: editFormId || undefined,
        form_name: formName.trim(),
        file: file || undefined,
        file_id: editFileId || undefined,
        surgeon_ids: selectedSurgeonIds
      });
      setIsAddOpen(false);
      resetDialogState();
      await fetchForms(searchText);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSearchText = (event: any): void => {
    const text = event.target.value || '';
    setSearchText(text);
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = window.setTimeout(() => {
      fetchForms(text);
    }, 500);
  };

  return (
    <div className="surgeon-container">
      <div className="surgeon-view">
        <div className="surgeon-text">{t('SURGEON_FORMS.TITLE')}</div>
        <div className="search-main-view">
          <div className={`search-width ${!loading && 'search-index'}`}>
            <SearchField
              placeholder={t('SURGEON_FORMS.SEARCH_PLACEHOLDER', 'Search by form name')}
              onChange={handleSearchText}
            />
          </div>
          <Button
            variant="contained"
            className={classes.addButton}
            onClick={openAddDialog}
          >
            {t('SURGEON_FORMS.ADD_FORM')}
          </Button>
        </div>
      </div>
      <div className="table-container">
        <MaterialTable
          icons={tableIcons}
          style={stylesObject}
          isLoading={loading}
          data={forms}
          options={optionsObject}
          columns={[
            {
              title: t('SURGEON_FORMS.FORM_NAME'),
              field: 'form_name',
              headerStyle: { textAlign: 'left', textTransform: 'uppercase' }
            },
            {
              title: t('SURGEON_FORMS.CREATED_ON'),
              field: 'created_on',
              headerStyle: { textAlign: 'left', textTransform: 'uppercase' },
              render: rowData =>
                DateTimeUtils.formatDateDMYWithSlashesAndZeros(
                  (rowData as SurgeonFormRow).created_on
                )
            },
            {
              title: t('SURGEON_FORMS.ASSIGNED_SURGEONS_COUNT'),
              field: 'assigned_surgeons_count',
              headerStyle: { textAlign: 'left', textTransform: 'uppercase' }
            },
            {
              title: t('SURGEON_FORMS.ACTIONS'),
              field: 'actions',
              headerStyle: { textAlign: 'left', textTransform: 'uppercase' },
              render: rowData => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Link
                    role="button"
                    tabIndex={0}
                    className={classes.actionLink}
                    onClick={() => handleView(rowData as SurgeonFormRow)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleView(rowData as SurgeonFormRow);
                      }
                    }}
                  >
                    {t('SURGEON_FORMS.VIEW', 'View')}
                  </Link>
                  <Link
                    role="button"
                    tabIndex={0}
                    className={classes.actionLink}
                    onClick={() => handleEdit(rowData as SurgeonFormRow)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleEdit(rowData as SurgeonFormRow);
                      }
                    }}
                  >
                    {t('COMMON.EDIT')}
                  </Link>
                  <Link
                    role="button"
                    tabIndex={0}
                    className={classes.actionLink}
                    onClick={() => handleDownload(rowData as SurgeonFormRow)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleDownload(rowData as SurgeonFormRow);
                      }
                    }}
                  >
                    {t('SURGEON_FORMS.DOWNLOAD')}
                  </Link>
                </div>
              )
            }
          ]}
          components={{
            Container: props => <div {...props} className={classes.root} />
          }}
        />
      </div>
      <Dialog
        open={isAddOpen}
        onClose={() => {
          setIsAddOpen(false);
          resetDialogState();
        }}
        maxWidth="sm"
        fullWidth
        className={classes.dialog}
      >
        <DialogContent>
          <div className="title-container">
            <div className="title-view">
              <span className="title-text">{t('SURGEON_FORMS.ADD_FORM')}</span>
            </div>
            <div className="close-view">
              <Link
                component="button"
                variant="body2"
                onClick={() => {
                  setIsAddOpen(false);
                  resetDialogState();
                }}
              >
                <span className="close-view-text">{t('PROCEDUREFLOWHEADER.CLOSE')}</span>
              </Link>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: '1rem' }}>
            <TextField
              label={t('SURGEON_FORMS.FORM_NAME')}
              value={formName}
              onChange={e => setFormName(e.target.value)}
              fullWidth
              variant="outlined"
              className={classes.formField}
            />
            <FormControl variant="outlined" fullWidth className={classes.formField}>
              <InputLabel id="assigned-to-label">
                {t('SURGEON_FORMS.ASSIGNED_TO', 'Assigned To')}
              </InputLabel>
              <Select
                labelId="assigned-to-label"
                multiple
                value={selectedSurgeonIds}
                onChange={event => setSelectedSurgeonIds(event.target.value as number[])}
                label={t('SURGEON_FORMS.ASSIGNED_TO', 'Assigned To')}
                renderValue={selected =>
                  surgeonOptions
                    .filter(option => (selected as number[]).includes(option.id))
                    .map(option =>
                      option.surgeon_id != null && String(option.surgeon_id).trim().length > 0
                        ? `${option.name} (${option.surgeon_id})`
                        : option.name
                    )
                    .join(', ')
                }
              >
                {surgeonOptions.map(option => (
                  <MenuItem key={option.id} value={option.id} className={classes.selectMenuItem}>
                    <Checkbox checked={selectedSurgeonIds.includes(option.id)} color="primary" />
                    <ListItemText
                      primary={
                        option.surgeon_id != null && String(option.surgeon_id).trim().length > 0
                          ? `${option.name} (${option.surgeon_id})`
                          : option.name
                      }
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <div>
              <div className={classes.fileInputLabel}>
                {editFormId
                  ? t('SURGEON_FORMS.UPDATE_FILE', 'Update File')
                  : t('SURGEON_FORMS.CHOOSE_FILE', 'Choose File')}
              </div>
              <input
                className={classes.fileInput}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={e => {
                  const input = e.target as HTMLInputElement;
                  setFile((input.files && input.files[0]) || null);
                }}
              />
              {(file || editFileName) && (
                <div className={classes.selectedFileName}>{file ? file.name : editFileName}</div>
              )}
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSubmitNewForm}
            variant="contained"
            className={`${classes.actionButton} ${classes.saveButton}`}
            disabled={submitting || !formName.trim() || (!file && !editFileId)}
          >
            {editFormId ? t('COMMON.UPDATE') : t('COMMON.SUBMIT')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SurgeonForms;